import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export type GlKind = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

export interface GlAccount {
  id: string;
  code: string;
  name: string;
  kind: GlKind;
  parent_id: string | null;
  is_active: boolean;
}

export interface GlJournal {
  id: string;
  posted_on: string;
  kind: string;
  source_id: string | null;
  memo: string | null;
  reversed_by: string | null;
  created_at: string;
}

export interface GlLine {
  id: string;
  account_id: string;
  debit_cents: string;
  credit_cents: string;
  currency: string;
  base_amount_cents: string;
  memo: string | null;
  vendor_id: string | null;
}

export interface AccountBalance {
  debitCents: string;
  creditCents: string;
  netDebitCents: string;
}

export function useGlAccounts(wsId: string | undefined) {
  return useQuery<GlAccount[]>({
    queryKey: ['gl-accounts', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<GlAccount[]>(`/workspaces/${wsId}/ledger/accounts`)).data,
  });
}

export function useGlAccount(wsId: string | undefined, id: string | null | undefined) {
  return useQuery<{ account: GlAccount; balance: AccountBalance }>({
    queryKey: ['gl-account', wsId, id],
    enabled: !!wsId && !!id,
    queryFn: async () =>
      (await api.get(`/workspaces/${wsId}/ledger/accounts/${id}`)).data,
  });
}

export function useUpdateGlAccount(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{ name: string; kind: GlKind; parentId: string | null; isActive: boolean }>;
    }) => (await api.patch<GlAccount>(`/workspaces/${wsId}/ledger/accounts/${id}`, patch)).data,
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['gl-accounts', wsId] });
      qc.invalidateQueries({ queryKey: ['gl-account', wsId, id] });
    },
  });
}

export function useGlJournals(wsId: string | undefined) {
  return useQuery<GlJournal[]>({
    queryKey: ['gl-journals', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<GlJournal[]>(`/workspaces/${wsId}/ledger/journals`)).data,
  });
}

export function useGlJournal(wsId: string | undefined, id: string | null | undefined) {
  return useQuery<{ journal: GlJournal; lines: GlLine[] }>({
    queryKey: ['gl-journal', wsId, id],
    enabled: !!wsId && !!id,
    queryFn: async () =>
      (await api.get(`/workspaces/${wsId}/ledger/journals/${id}`)).data,
  });
}

export function useReverseJournal(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<GlJournal>(`/workspaces/${wsId}/ledger/journals/${id}/reverse`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['gl-journals', wsId] });
      qc.invalidateQueries({ queryKey: ['gl-journal', wsId, id] });
    },
  });
}
